/* eslint-disable max-len*/
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import {
  GridColDef,
  GridEditInputCell,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
  GridRowsProp,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import ActionButton from 'src/components/ActionButton/ActionButton';
import FormDropdown from 'src/components/FormInputs/FormDropdown/FormDropdown';
import { abortPromiseOnUnmount } from 'src/services/base.service';
import { updateSalaryScale } from 'src/services/salaryScale.service';
import { FormDropdownChangeEventConfig } from 'src/types/propTypes/FormDropdownPropTypes';
import { SalaryScaleListItemModel } from 'src/types/SalaryScaleListItemModel';
import { SalaryScaleValidationSchema } from 'src/types/Validation/SalaryScaleValidationSchema';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import Modal from '../../../../components/Modal/Modal';
import StyledGrid from '../../../../components/StyledGrid/StyledGrid';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
  defaultAdditionalSalaryState,
  selectAdditionalSalary,
  selectMinimumSalarySchedule,
  selectSalaryScaleListItems,
  selectSalaryScaleLoadingFormViewModel,
  selectSalaryScaleLoadingListItems,
  selectSalaryScaleLoadingSchoolYears,
  selectSalaryScaleSchoolYearOptions,
  selectSelectedSchoolYearOption,
  selectUpdatedListItems,
  setAdditionalSalary,
  setDefaultUpdatedListItems,
  setIsListItemLoading,
  setSchoolYearFilter,
  setSelectedSchoolYearOption,
  setUpdatedListItems,
} from '../../../../redux/slices/salaryScaleSlice';
import {
  fetchMinSalaryScaleListItems,
  fetchSalaryScaleListItems,
  fetchSalaryScaleSchoolYears,
  updateAllSalaryScaleListItems,
} from '../../../../redux/thunks/salaryScaleThunks';
import { DialogType } from '../../../../types/DialogTypes';
import { formatToDateTimeString } from '../../../../utilities/dateUtilities';
import {
  isDistrictAdmin,
  isSPO,
  isStateAdmin,
} from '../../../../utilities/userUtilities';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './SalaryScaleList.css';

const SALARY_EDIT_FIELDS = [
  'aAssociateDegreeSalary',
  'aaBachelorDegreeSalary',
  'aaaMasterDegreeSalary',
  'aaaaPhDDegreeSalary',
] as const;

const comparableSalaryValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const cleaned = String(value).replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  if (!Number.isNaN(n)) {
    return n.toFixed(2);
  }
  return cleaned;
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

//eslint-disable-next-line
function NameEditInputCell(props: GridRenderEditCellParams): any {
  const { error } = props;

  return (
    <StyledTooltip open={!!error} title={error}>
      <GridEditInputCell {...props} />
    </StyledTooltip>
  );
}

//eslint-disable-next-line
function renderEditName(params: GridRenderEditCellParams): any {
  return <NameEditInputCell {...params} />;
}

const SalaryScaleList = (): ReactElement => {
  const selectedSchoolYearOption = useAppSelector(
    selectSelectedSchoolYearOption
  );
  const schoolYearOptions = useAppSelector(selectSalaryScaleSchoolYearOptions);
  const schoolYear = selectedSchoolYearOption?.value || '';
  const isListItemsLoading = useAppSelector(selectSalaryScaleLoadingListItems);
  const isFormViewLoading = useAppSelector(
    selectSalaryScaleLoadingFormViewModel
  );
  const isSchoolYearsLoading = useAppSelector(
    selectSalaryScaleLoadingSchoolYears
  );
  const listItems = useAppSelector(selectSalaryScaleListItems);
  const updatedList: SalaryScaleListItemModel[] = useAppSelector(
    selectUpdatedListItems
  );
  const addlSalary: string = useAppSelector(selectAdditionalSalary);
  const minimumSalarySchedule = useAppSelector((state) =>
    selectMinimumSalarySchedule(state, selectedSchoolYearOption?.text || '')
  );

  const isLoading =
    isListItemsLoading || isFormViewLoading || isSchoolYearsLoading;
  const dispatch = useAppDispatch();
  const apiRef = useGridApiRef();
  /** 28729: onStateChange fires constantly; only sync when editRows actually changes. */
  const lastEditRowsSignatureRef = useRef<string | null>(null);
  const [isPreviousYearsDisabled, setIsPreviousYearsDisabled] = useState(false);
  const [pendingEditDirty, setPendingEditDirty] = useState(false);
  const [pendingEditHasError, setPendingEditHasError] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const regExpression = new RegExp(/^(?=.*\d)-?(\d+)?(\.\d{1,2})?$/);

  // Fetch school year options first
  useEffect(() => {
    const promise: unknown = dispatch(fetchSalaryScaleSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    if (schoolYear === '' && schoolYearOptions.length > 0) {
      const mostRecentYear = schoolYearOptions[0];
      dispatch(setSelectedSchoolYearOption(mostRecentYear));
      dispatch(setSchoolYearFilter(mostRecentYear.value));
    }
  }, [dispatch, schoolYear, schoolYearOptions]);

  useEffect(() => {
    let promise: unknown;
    if (schoolYear) {
      dispatch(fetchMinSalaryScaleListItems(schoolYear));
      promise = dispatch(fetchSalaryScaleListItems(schoolYear));
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch, schoolYear, refreshData]);

  const [showApplyToAllModal, setShowAllyToAllModal] = useState(false);
  const [showCancelModal, setCancelModal] = useState(false);

  const handleYesInCancel = (): void => {
    setCancelModal(false);
    dispatch(setDefaultUpdatedListItems());
    setRefreshData(!refreshData);
  };

  const handleNoInCancel = (): void => {
    setCancelModal(false);
  };

  const handleCancel = (): void => {
    setCancelModal(true);
  };

  const syncEditButtonState = useCallback((): void => {
    const api = apiRef.current;
    if (!api?.state) {
      return;
    }
    const editRows = (api.state.editRows ?? {}) as Record<
      string,
      Record<string, { value?: unknown; error?: string | null }>
    >;
    const editRowsSignature = JSON.stringify(editRows);
    if (editRowsSignature === lastEditRowsSignatureRef.current) {
      return;
    }
    lastEditRowsSignatureRef.current = editRowsSignature;

    const rowIds = Object.keys(editRows);
    if (rowIds.length === 0) {
      setPendingEditDirty(false);
      setPendingEditHasError(false);
      return;
    }

    let anyDirty = false;
    let anyError = false;

    for (const rowId of rowIds) {
      const rowFields = editRows[rowId];
      if (!rowFields) {
        continue;
      }

      if (Object.values(rowFields).some((cell) => Boolean(cell?.error))) {
        anyError = true;
      }

      const baseRow = listItems.find((r) => String(r.id) === String(rowId));
      if (!baseRow) {
        continue;
      }

      for (const field of SALARY_EDIT_FIELDS) {
        const cell = rowFields[field];
        const baseVal = baseRow[field];
        const editedVal =
          cell !== undefined && cell.value !== undefined ? cell.value : baseVal;
        if (
          comparableSalaryValue(editedVal) !== comparableSalaryValue(baseVal)
        ) {
          anyDirty = true;
          break;
        }
      }
    }

    setPendingEditDirty((prev) => (prev === anyDirty ? prev : anyDirty));
    setPendingEditHasError((prev) => (prev === anyError ? prev : anyError));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems]);

  useEffect(() => {
    lastEditRowsSignatureRef.current = null;
    syncEditButtonState();
  }, [syncEditButtonState]);

  const hasDataChanges = updatedList.length > 0 || pendingEditDirty;
  const isCancelDisabled = !hasDataChanges;
  const isSaveDisabled = updatedList.length === 0 || pendingEditHasError;

  const handleSave = async (): Promise<void> => {
    try {
      dispatch(setIsListItemLoading(true));
      await updateSalaryScale(updatedList);
      dispatch(
        createNotification({
          severity: 'success',
          children: 'Salary Scale edited',
        })
      );
    } catch {
      dispatch(
        createNotification({
          severity: 'error',
          children: 'Salary Scale edit failed, resetting values',
        })
      );
    }
    dispatch(setDefaultUpdatedListItems());
    setRefreshData(!refreshData);
  };

  const handleUpdateAll = async (): Promise<void> => {
    const paramsObj = {
      schoolYear: schoolYear,
      additionalSalary: addlSalary,
    };
    setShowAllyToAllModal(false);
    await dispatch(updateAllSalaryScaleListItems(paramsObj));
    setRefreshData(!refreshData);
  };

  //eslint-disable-next-line
  const handleApplyToAll = (values: any, resetForm: any): void => {
    let salValue = values.additionalSalary;
    if (salValue.charAt(0) === '$') {
      salValue = Number(salValue.substring(1)).toString();
    }
    dispatch(setAdditionalSalary(salValue.toString()));

    resetForm(defaultAdditionalSalaryState);
    setShowAllyToAllModal(true);
  };

  // eslint-disable-next-line
  const processRowUpdate = (newRow: any, oldRow: any): any => {
    if (JSON.stringify(oldRow) !== JSON.stringify(newRow)) {
      dispatch(setUpdatedListItems(newRow));
    }
    return newRow;
  };

  const updateAdditionalSalary = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const defaultAdditionalSalary = '0.00';
    /* istanbul ignore next */
    if (config.setValueHook && defaultAdditionalSalary) {
      config.setValueHook('additionalSalary', defaultAdditionalSalary);
    }

    if (config.value) {
      const selectedOption = schoolYearOptions.find(
        (option) => option.value === config.value
      );

      if (selectedOption) {
        const showValue =
          selectedOption.value === schoolYearOptions[0]?.value ||
          selectedOption.value === schoolYearOptions[1]?.value
            ? false
            : true;
        setIsPreviousYearsDisabled(showValue);
        dispatch(setSelectedSchoolYearOption(selectedOption));
        dispatch(setSchoolYearFilter(selectedOption.value));
        dispatch(setDefaultUpdatedListItems());
      }
    }
  };

  const handleDialogClose = (): void => {
    setShowAllyToAllModal(false);
  };

  const getMinimumSalaryScheduleByYearsOfExperience = (
    yoe: string
  ): SalaryScaleListItemModel[] => {
    const minSalary = minimumSalarySchedule?.filter(
      (eachRecord) => eachRecord.yearsOfExperience === yoe
    );

    return minSalary;
  };

  const isParamValid = (
    paramValue: number,
    stateMinSal: number
  ): string | null => {
    let isError;

    if (!regExpression.test(paramValue.toString())) {
      isError = 'Must be in this format 00000.00 without commas or $ symbol';
    } else {
      isError =
        paramValue < stateMinSal
          ? `Selected value cannot be lower than the State minimum salary $${stateMinSal.toLocaleString()}`
          : null;
    }

    return isError;
  };

  const preProcessEditCellPropsA = (
    params: GridPreProcessEditCellProps
    //eslint-disable-next-line
  ): any => {
    const stateMinimumSalaryFoAssociateDegree = Number(
      getMinimumSalaryScheduleByYearsOfExperience(
        params.row.yearsOfExperience
      )[0]?.aAssociateDegreeSalary
    );

    const errorMessage = isParamValid(
      params.props.value,
      stateMinimumSalaryFoAssociateDegree
    );

    return { ...params.props, error: errorMessage };
  };

  const preProcessEditCellPropsAA = (
    params: GridPreProcessEditCellProps
    //eslint-disable-next-line
  ): any => {
    const stateMinimumSalaryForBachelorsDegree = Number(
      getMinimumSalaryScheduleByYearsOfExperience(
        params.row.yearsOfExperience
      )[0]?.aaBachelorDegreeSalary
    );

    const errorMessage = isParamValid(
      params.props.value,
      stateMinimumSalaryForBachelorsDegree
    );

    return { ...params.props, error: errorMessage };
  };

  const preProcessEditCellPropsAAA = (
    params: GridPreProcessEditCellProps
    //eslint-disable-next-line
  ): any => {
    const stateMinimumSalaryForMastersDegree = Number(
      getMinimumSalaryScheduleByYearsOfExperience(
        params.row.yearsOfExperience
      )[0]?.aaaMasterDegreeSalary
    );

    const errorMessage = isParamValid(
      params.props.value,
      stateMinimumSalaryForMastersDegree
    );

    return { ...params.props, error: errorMessage };
  };

  //eslint-disable-next-line
  const preProcessEditCellPropsAAAA = (
    params: GridPreProcessEditCellProps
    //eslint-disable-next-line
  ): any => {
    const stateMinimumSalaryForPhDDegree = Number(
      getMinimumSalaryScheduleByYearsOfExperience(
        params.row.yearsOfExperience
      )[0]?.aaaaPhDDegreeSalary
    );

    const errorMessage = isParamValid(
      params.props.value,
      stateMinimumSalaryForPhDDegree
    );

    return { ...params.props, error: errorMessage };
  };

  const columns: GridColDef[] = [
    {
      field: 'yearsOfExperience',
      headerName: 'Years of Experience',
      type: 'number',
      editable: false,
      align: 'center',
      headerAlign: 'left',
      width: 150,
    },
    {
      field: 'aAssociateDegreeSalary',
      headerName: "A - Bachelor's Degree",
      type: 'string',
      editable:
        !isPreviousYearsDisabled &&
        (isDistrictAdmin() || isStateAdmin() || isSPO()),
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      valueFormatter: (cellValues) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(cellValues);
      },
      preProcessEditCellProps: preProcessEditCellPropsA,
      renderEditCell: renderEditName,
    },
    {
      field: 'aaBachelorDegreeSalary',
      headerName: "AA - Master's Degree",
      type: 'string',
      editable:
        !isPreviousYearsDisabled &&
        (isDistrictAdmin() || isStateAdmin() || isSPO()),
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      valueFormatter: (cellValues) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(cellValues);
      },
      preProcessEditCellProps: preProcessEditCellPropsAA,
      renderEditCell: renderEditName,
    },
    {
      field: 'aaaMasterDegreeSalary',
      headerName: "AAA - Specialist's Degree",
      type: 'string',
      editable:
        !isPreviousYearsDisabled &&
        (isDistrictAdmin() || isStateAdmin() || isSPO()),
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      valueFormatter: (cellValues) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(cellValues);
      },
      preProcessEditCellProps: preProcessEditCellPropsAAA,
      renderEditCell: renderEditName,
    },
    {
      field: 'aaaaPhDDegreeSalary',
      headerName: 'AAAA - Doctorate Degree',
      type: 'string',
      editable:
        !isPreviousYearsDisabled &&
        (isDistrictAdmin() || isStateAdmin() || isSPO()),
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      valueFormatter: (cellValues) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(cellValues);
      },
      preProcessEditCellProps: preProcessEditCellPropsAAAA,
      renderEditCell: renderEditName,
    },
  ];
  const rows: GridRowsProp = listItems;

  const cancelTooltip = (): string => {
    return hasDataChanges
      ? 'Reset table data'
      : '** No changes have been made **';
  };

  const saveTooltip = (): string => {
    if (updatedList.length === 0) {
      return '** No changes have been made **';
    }
    if (pendingEditHasError) {
      return 'Fix validation errors before saving';
    }
    return 'Save Changes';
  };

  const applyToAllTooltip = (isValid: boolean, dirty: boolean): string => {
    const retVal =
      isValid && dirty
        ? 'Apply to all records'
        : '** No additional salary provided **';
    return retVal;
  };

  const getApplyToAllDisabled = (
    isValid: boolean,
    dirty: boolean
  ): boolean | undefined => {
    return !isValid || !dirty;
  };

  const getMostRecentUpdatedRecord = (): SalaryScaleListItemModel | null => {
    if (!listItems || listItems.length === 0) {
      return null;
    }

    const recordsWithDates = listItems.filter((item) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dateValue = item.lastUpdatedDate || (item as any).LastUpdatedDate;
      return dateValue && dateValue.trim() !== '';
    });

    if (recordsWithDates.length === 0) {
      return null;
    }

    return recordsWithDates.reduce((mostRecent, current) => {
      const mostRecentDate =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mostRecent.lastUpdatedDate || (mostRecent as any).LastUpdatedDate || '';

      const currentDate =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        current.lastUpdatedDate || (current as any).LastUpdatedDate || '';

      if (!mostRecentDate || mostRecentDate.trim() === '') {
        return current;
      }
      if (!currentDate || currentDate.trim() === '') {
        return mostRecent;
      }

      try {
        const currentDateObj = new Date(currentDate);
        const mostRecentDateObj = new Date(mostRecentDate);

        if (isNaN(currentDateObj.getTime())) {
          return mostRecent;
        }
        if (isNaN(mostRecentDateObj.getTime())) {
          return current;
        }

        return currentDateObj > mostRecentDateObj ? current : mostRecent;
      } catch {
        return mostRecent;
      }
    });
  };

  const mostRecentRecord = getMostRecentUpdatedRecord();

  const formatDateToCST = (dateString: string | undefined): string => {
    if (!dateString || dateString.trim() === '') {
      return '';
    }

    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const dt = DateTime.fromJSDate(date, { zone: 'utc' });
        if (dt.isValid) {
          return dt.setZone('America/Chicago').toLocaleString({
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          });
        }
      }

      const formatted = formatToDateTimeString(dateString);
      if (formatted) {
        return formatted;
      }
    } catch (error) {
      console.error('Error parsing date:', error, 'Date string:', dateString);
    }

    return '';
  };

  const dateString =
    mostRecentRecord?.lastUpdatedDate ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mostRecentRecord as any)?.LastUpdatedDate ||
    '';

  const formattedLastUpdatedDate = formatDateToCST(dateString);

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        validateOnMount={true}
        initialValues={{
          ...defaultAdditionalSalaryState,
          schoolYear:
            selectedSchoolYearOption?.value ||
            defaultAdditionalSalaryState.schoolYear,
        }}
        validationSchema={SalaryScaleValidationSchema}
        onSubmit={handleSave}
      >
        {({ dirty, isValid, values, resetForm }) => (
          <Form>
            <DistrictSchoolInfoPage
              pageClass="data-input--district-school-info--salary-scale--list"
              isLoading={isLoading}
              loadingDataId={'salary-scale-list-loader'}
            >
              <div className="content-outer-container">
                <div className="content-inner-container">
                  <div className="header-section">
                    <div>
                      <p>
                        To apply a{' '}
                        <strong>standard amount to all salaries</strong>, use
                        the Additional Salary field and select{' '}
                        <strong>&#39;Apply to All&#39;</strong>.
                        <br />
                        To adjust a <strong>singular salary amount</strong>,
                        click in the cell to edit the value.
                      </p>
                    </div>
                    {mostRecentRecord && (
                      <div className="last-updated-info">
                        <div className="last-updated-field">
                          Last Updated By:{' '}
                          {mostRecentRecord.lastUpdatedBy || 'N/A'}
                        </div>
                        <div className="last-updated-field">
                          Last Updated Date: {formattedLastUpdatedDate || 'N/A'}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="button-group">
                    <ActionButton
                      classes="button no-wrap-text cancel-button"
                      onClick={handleCancel}
                      dataTestId="cancel-button"
                      tooltipText={cancelTooltip()}
                      disabled={
                        isCancelDisabled ||
                        !(isDistrictAdmin() || isStateAdmin() || isSPO())
                      }
                    >
                      <span>Cancel</span>
                    </ActionButton>
                    <ActionButton
                      classes="button--secondary submit-button"
                      onClick={handleSave}
                      dataTestId="save-button"
                      tooltipText={saveTooltip()}
                      disabled={
                        isSaveDisabled ||
                        !(isDistrictAdmin() || isStateAdmin() || isSPO())
                      }
                    >
                      <span>Save</span>
                    </ActionButton>
                  </div>

                  <div className="school-year-selection-container">
                    <FormDropdown
                      displayName="School Year"
                      field="schoolYear"
                      options={schoolYearOptions}
                      onChangeEvent={updateAdditionalSalary}
                    />
                    <FormTextbox
                      displayName="Additional Salary"
                      field="additionalSalary"
                      disabled={
                        isPreviousYearsDisabled ||
                        !(isDistrictAdmin() || isStateAdmin() || isSPO())
                      }
                      validationSchema={SalaryScaleValidationSchema}
                    />

                    <ActionButton
                      classes="button--secondary no-wrap-text apply-all-button "
                      dataTestId="apply-to-all"
                      onClick={() => handleApplyToAll(values, resetForm)}
                      tooltipText={applyToAllTooltip(isValid, dirty)}
                      disabled={
                        getApplyToAllDisabled(isValid, dirty) ||
                        !(isDistrictAdmin() || isStateAdmin() || isSPO())
                      }
                    >
                      <>+ Apply to All</>
                    </ActionButton>
                  </div>

                  <div className="table-container">
                    <StyledGrid
                      apiRef={apiRef}
                      editMode="row"
                      rows={rows}
                      columns={columns}
                      processRowUpdate={processRowUpdate}
                      onStateChange={syncEditButtonState}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onProcessRowUpdateError={(error: any) =>
                        console.log(error)
                      }
                    />
                  </div>
                </div>
              </div>
            </DistrictSchoolInfoPage>
          </Form>
        )}
      </Formik>

      {showCancelModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showCancelModal}
          onClose={handleDialogClose}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              Your changes have not been saved. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleYesInCancel}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleNoInCancel}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}

      {showApplyToAllModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showApplyToAllModal}
          onClose={handleDialogClose}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              You are about to apply{' '}
              {addlSalary.startsWith('-')
                ? `-$${addlSalary.substring(1)}`
                : `$${addlSalary}`}{' '}
              to all rows for School Year {schoolYear}. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleUpdateAll}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleDialogClose}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SalaryScaleList;
